import type { TierDistribution } from '../index.js'
import type { WorkerTier } from '../../core/index.js'
import { TIER_LABELS, WorkersIcon } from '../../core/index.js'

export const TIER_COLORS: Record<WorkerTier, string> = {
  gold: 'bg-gold text-white',
  silver: 'bg-silver text-white',
  bronze: 'bg-bronze text-white',
  probationary: 'bg-prob text-white',
}

// A tier with anyone in it always shows at least this much bar, so a single worker in a large facility is not invisible.
const MIN_VISIBLE_BAR_PCT = 1

/**
 * One bar per tier. The count and percentage sit beside the bar rather than
 * inside it: a label inside the fill has to fit the fill, and at 1% the
 * fill is a few pixels wide.
 */
export function TierDistributionBars({ tiers }: { tiers: TierDistribution['tiers'] }) {
  return (
    <div className="flex flex-col gap-2">
      {tiers.map((entry) => (
        <div key={entry.tier} className="flex items-center gap-3">
          <div className="w-24 text-right text-xs font-bold">{TIER_LABELS[entry.tier]}</div>
          <div
            className="flex-1 h-6 bg-gray-100 border border-marine/15 overflow-hidden"
            role="img"
            aria-label={`${TIER_LABELS[entry.tier]}: ${entry.count} of the roster, ${entry.pct}%`}
          >
            <div className={`h-full ${TIER_COLORS[entry.tier]}`} style={{ width: `${entry.count > 0 ? Math.max(entry.pct, MIN_VISIBLE_BAR_PCT) : 0}%` }} />
          </div>
          <div className="w-28 shrink-0 flex items-center gap-1.5 whitespace-nowrap tabular-nums">
            {/* Icon and count are one right-aligned unit, so they stay together while the percentages still line up. */}
            <span className="w-12 flex items-center justify-end gap-1">
              <span className="text-ink-mid" aria-hidden="true">
                <WorkersIcon />
              </span>
              <span className="text-sm font-black text-ink">{entry.count}</span>
            </span>
            <span className="text-xs text-ink-mid">{entry.pct}%</span>
          </div>
        </div>
      ))}
    </div>
  )
}
